import { PdfViewer, usePdfFile } from '@acme/features';
import {
  BreadcrumbItem,
  Field,
  Separator,
  formatDateTime,
} from '@acme/foundation';
import {
  $application,
  applicationOpened,
  applicationReset,
} from '@entities/application';
import { useUnit } from 'effector-react';
import { Button, Card, Checkbox, Label } from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetApplicationBreadcrumbs } from '../../application/ui/applicationLayout';

export const ApplicationContractPage = () => {
  const { id, actionId } = useParams();
  const navigate = useNavigate();

  const { application, onOpened, onReset } = useUnit({
    application: $application,
    onOpened: applicationOpened,
    onReset: applicationReset,
  });

  const onSuccessSign = () => {
    navigate(`/application/${application?.id}`);
  };

  const {
    loadPdfFile,
    getDocument,
    blob,
    isLoading: isLoadingPdfFile,
    isDocumentLoading,
    error,
    handleSignAction,
  } = usePdfFile({ onSuccessSign });

  const [isChecked, setIsChecked] = useState(false);

  const handleSignActionFx = () => {
    if (actionId) {
      handleSignAction(actionId);
    }
  };

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      {
        label: 'Главная',
        onClick: () => navigate('/dashboard'),
      },
      ...(application
        ? [
            {
              label: application.code ?? '',
              onClick: () => navigate(`/application/${application.id}`),
            },
            {
              label: 'Договор на оказание услуг по трансграничному переводу',
            },
          ]
        : [
            {
              label: 'Заявка не найдена',
            },
          ]),
    ],
    [navigate, application]
  );

  useEffect(() => {
    if (!id) return;
    onOpened({ id });
    return () => onReset();
  }, [id, onOpened, onReset]);

  useSetApplicationBreadcrumbs(breadcrumbItems);

  useEffect(() => {
    if (actionId) {
      getDocument(actionId);
    }
  }, [actionId]);

  return (
    <Card>
      <div className="flex gap-4 justify-between items-center">
        <h2 className="font-semibold text-xl">
          Договор на оказание услуг по трансграничному переводу
        </h2>

        <Button
          onClick={loadPdfFile}
          color="alternative"
          disabled={isLoadingPdfFile}
        >
          Скачать
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-6">
        <Field label="Номер заявки" value={application?.code} />
        <Field
          label="Дата создания"
          value={formatDateTime(application?.createdAt)}
        />
      </div>

      <Separator className="-mx-6" />

      <div className="mb-6">
        {isDocumentLoading && (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Загрузка документа...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center p-8 text-red-500">
            {error}
          </div>
        )}

        {blob && !isDocumentLoading && !error && (
          <PdfViewer file={blob} className="overflow-y-auto" />
        )}
      </div>

      <Separator className="-mx-6" />

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Label className="flex gap-2 items-center">
            <Checkbox onChange={(v) => setIsChecked(v.target.checked)} />
            <span>Я ознакомился и согласен с условиями договора</span>
          </Label>
        </div>

        <Button
          onClick={handleSignActionFx}
          color="primary"
          disabled={!isChecked}
        >
          Подписать КЭП
        </Button>
      </div>
    </Card>
  );
};
